import { useState, useRef } from 'react';

export const usePrint = () => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const currentUrlRef = useRef(null);
    const printResourcesRef = useRef([]);

    const printPDF = async (serviceCall) => {
        let url;
        try {
            setIsGenerating(true);
            const response = await serviceCall();

            const blob = response.data ? response.data : response;

            if (!(blob instanceof Blob)) {
                throw new Error('Response is not a valid Blob');
            }

            url = URL.createObjectURL(blob);
            // Revoke any previously open preview URL
            if (currentUrlRef.current) {
                URL.revokeObjectURL(currentUrlRef.current);
            }
            currentUrlRef.current = url;
            setPreviewUrl(url);
        } catch (error) {
            console.error('Print error:', error);
            if (url) {
                URL.revokeObjectURL(url);
            }
            throw error;
        } finally {
            setIsGenerating(false);
        }
    };

    const closePreview = () => {
        if (currentUrlRef.current) {
            URL.revokeObjectURL(currentUrlRef.current);
            currentUrlRef.current = null;
        }
        setPreviewUrl(null);
    };

    const printSimpleHTML = () => {
        console.log('Starting simple HTML print test...');
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.top = '0';
        iframe.style.left = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        iframe.style.zIndex = '9999';
        iframe.style.opacity = '0';
        
        document.body.appendChild(iframe);
        
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Test Print</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        padding: 20px;
                    }
                    h1 {
                        color: #333;
                    }
                    .test-content {
                        margin: 20px 0;
                        padding: 20px;
                        border: 2px solid #333;
                    }
                </style>
            </head>
            <body>
                <h1>Print Test Document</h1>
                <div class="test-content">
                    <p>This is a simple HTML print test.</p>
                    <p>If you see this in the print dialog, the print mechanism works!</p>
                    <p>Date: ${new Date().toLocaleString()}</p>
                </div>
            </body>
            </html>
        `;
        
        iframe.onload = () => {
            console.log('Simple HTML iframe loaded');
            try {
                const doc = iframe.contentDocument || iframe.contentWindow.document;
                doc.open();
                doc.write(htmlContent);
                doc.close();
                
                // Wait a bit for content to render
                setTimeout(() => {
                    console.log('Attempting to print simple HTML...');
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                    console.log('Print dialog triggered');
                    
                    // Clean up after print
                    setTimeout(() => {
                        if (document.body.contains(iframe)) {
                            document.body.removeChild(iframe);
                        }
                    }, 1000);
                }, 100);
            } catch (e) {
                console.error('Simple HTML print failed:', e);
                if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                }
            }
        };
        
        // For simple HTML, we can use about:blank
        iframe.src = 'about:blank';
    };

    const cleanup = () => {
        if (currentUrlRef.current) {
            URL.revokeObjectURL(currentUrlRef.current);
            currentUrlRef.current = null;
        }
        setPreviewUrl(null);
        printResourcesRef.current.forEach(({ url, iframe }) => {
            URL.revokeObjectURL(url);
            if (iframe && document.body.contains(iframe)) {
                document.body.removeChild(iframe);
            }
        });
        printResourcesRef.current = [];
    };

    return {
        printPDF,
        printSimpleHTML,
        previewUrl,
        closePreview,
        isGenerating,
        cleanup
    };
};